import { useCallback, useId, useRef, useState } from "react";

export interface RequiredField {
  key: string;
  /** Short field name for the «Не заповнено: …» summary. */
  label: string;
  missing: boolean;
  /** Text shown under the field when it is missing. */
  message: string;
}

const FOCUS_TARGET =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

/**
 * Create-drawer validation (§0 — no dead CTA): the submit button is NEVER
 * disabled for missing input. Pressing it with gaps calls `validate()` →
 * no request, scroll + focus to the first missing field, every missing field
 * gets `errorFor(key)` and the footer lists them via `missing`.
 *
 * Errors are DERIVED from the live `fields` each render (shown only after the
 * first attempt), so they disappear as soon as the user fills the field.
 */
export function useRequiredFields(fields: RequiredField[]) {
  const baseId = useId();
  const [attempted, setAttempted] = useState(false);
  const nodes = useRef(new Map<string, HTMLElement>());
  const refCallbacks = useRef(new Map<string, (el: HTMLElement | null) => void>());

  /** Attach to the field's wrapper (or the control itself) — the scroll/focus target. */
  const fieldRef = useCallback((key: string) => {
    let cb = refCallbacks.current.get(key);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) nodes.current.set(key, el);
        else nodes.current.delete(key);
      };
      refCallbacks.current.set(key, cb);
    }
    return cb;
  }, []);

  const focusField = useCallback((key: string) => {
    const el = nodes.current.get(key);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const target = el.matches(FOCUS_TARGET) ? el : el.querySelector<HTMLElement>(FOCUS_TARGET);
    target?.focus({ preventScroll: true });
  }, []);

  const missing = attempted ? fields.filter((f) => f.missing) : [];

  const errorFor = (key: string): string | undefined => missing.find((f) => f.key === key)?.message;

  const errorId = (key: string) => `${baseId}-${key}-error`;

  /** Props for the control: aria-invalid + aria-describedby while it has an error. */
  const invalidProps = (key: string) =>
    errorFor(key) ? { "aria-invalid": true as const, "aria-describedby": errorId(key) } : {};

  /** true → all required fields present; false → errors shown, first field focused. */
  const validate = (): boolean => {
    setAttempted(true);
    const first = fields.find((f) => f.missing);
    if (!first) return true;
    // after the error texts render (layout shifts) — then scroll/focus.
    requestAnimationFrame(() => focusField(first.key));
    return false;
  };

  const reset = useCallback(() => setAttempted(false), []);

  return { missing, errorFor, errorId, invalidProps, fieldRef, focusField, validate, reset };
}
